import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Input } from "../../../app/components/ui/input";
import { Label } from "../../../app/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/ui/select";
import { formatInr } from "../../../lib/currency";
import type { GstOverrideType, PosCartItem, PosGstLineBreakdown } from "../types";

export function PosCartItemGstPanel({
  item,
  gst,
  onChangeOverride,
}: {
  item: PosCartItem;
  gst: PosGstLineBreakdown;
  onChangeOverride: (patch: Partial<Pick<PosCartItem, "gst_override_type" | "gst_override_value" | "gst_override_reason">>) => void;
}) {
  const displayOverride = item.gst_override_type ?? "none";

  return (
    <div className="mt-3 space-y-3 border-t pt-3 text-sm">
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1">GST details</p>
        <p className="text-xs">
          GST rate:{" "}
          {gst.effectiveRatePercent != null ? `${gst.effectiveRatePercent}%` : "—"}
        </p>
        <p className="text-xs">CGST: {formatInr(gst.cgst)}</p>
        <p className="text-xs">SGST: {formatInr(gst.sgst)}</p>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Override GST (advanced)</Label>
        <Select
          value={displayOverride}
          onValueChange={(v) => {
            const next = v as GstOverrideType | "none";
            onChangeOverride({
              gst_override_type: next === "none" ? null : (next as GstOverrideType),
              gst_override_value: next === "manual_rate" ? item.gst_override_value ?? 18 : null,
              gst_override_reason: next === "none" ? "" : item.gst_override_reason,
            });
          }}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="No override" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No override</SelectItem>
            <SelectItem value="no_tax">Remove GST (not recommended)</SelectItem>
            <SelectItem value="manual_rate">Custom GST rate</SelectItem>
          </SelectContent>
        </Select>

        {displayOverride === "no_tax" ? (
          <Alert variant="destructive" className="py-2">
            <AlertTitle className="text-xs">Compliance</AlertTitle>
            <AlertDescription className="text-xs">
              This may violate GST rules and affect compliance.
            </AlertDescription>
          </Alert>
        ) : null}

        {displayOverride === "manual_rate" ? (
          <div className="space-y-1">
            <Label className="text-xs">Custom rate (%)</Label>
            <Input
              className="h-8 text-xs"
              inputMode="decimal"
              value={item.gst_override_value != null ? String(item.gst_override_value) : ""}
              onChange={(e) => {
                const raw = e.target.value;
                onChangeOverride({
                  gst_override_value: raw === "" ? null : Number.parseFloat(raw),
                });
              }}
            />
          </div>
        ) : null}

        {(displayOverride === "manual_rate" || displayOverride === "no_tax") && (
          <div className="space-y-1">
            <Label className="text-xs">Reason (required)</Label>
            <Input
              className="h-8 text-xs"
              value={item.gst_override_reason}
              onChange={(e) => onChangeOverride({ gst_override_reason: e.target.value })}
              placeholder="Document why this override is applied"
            />
          </div>
        )}
      </div>
    </div>
  );
}
